<script setup lang="ts">
// 1. Imports
import { onMounted, h, ref, VNode, computed } from 'vue';
import { useAdminUsersStore } from '@/stores/admin_users';
import {
  NSpace,
  NButton,
  NIcon,
  NPopconfirm,
  NTag,
  type DataTableColumns,
  type FormInst,
  type FormRules,
} from 'naive-ui';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import type { SortOrder } from 'naive-ui/es/data-table/src/interface';
import type { UserInDb, UserUpdateSuperuser, SearchQuery } from '@/api';
import { IconDelete, IconEdit, IconRefresh } from '@/components/design/icons';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

// UserInDb already includes is_active as boolean, so we can use it directly
type ExtendedUserInDb = UserInDb;

// 2. Store access
const store = useAdminUsersStore();
const message = useGaiaToast();

// 3. Local state
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const searchQuery = ref<string>('');
const sortKey = ref<string>('full_name');
const sortOrder = ref<'asc' | 'desc'>('asc');

// Edit form state
const showEditModal = ref<boolean>(false);
const editFormRef = ref<FormInst | null>(null);
const editFormModel = ref<UserUpdateSuperuser>({
  full_name: '',
  is_active: true,
});
const currentEditId = ref<string>('');
// Shown, never sent: the backend treats the address as identity and only sets
// it from a verified Firebase token, so no update payload carries an email.
const currentEditEmail = ref<string>('');

// Add a ref to store if current user being edited is admin
const isEditingAdmin = ref<boolean>(false);

// Form validation rules
const formRules: FormRules = {
  full_name: [
    {
      required: true,
      message: 'Full name is required',
      trigger: ['blur', 'input'],
    },
  ],
};

// Add this computed property for the edit form
const userIsActive = computed({
  get: () =>
    editFormModel.value.is_active === null ? false : Boolean(editFormModel.value.is_active),
  set: (val: boolean) => {
    editFormModel.value.is_active = val;
  },
});

// 5. Computed properties and table columns
const columns: DataTableColumns<ExtendedUserInDb> = [
  {
    title: 'Full Name',
    key: 'full_name',
    sorter: true,
  },
  {
    title: 'Email',
    key: 'email',
    sorter: true,
  },
  {
    title: 'Status',
    key: 'is_active',
    sorter: true,
    render(row): VNode {
      const isActive = row.is_active;

      return h(
        NTag,
        {
          type: isActive ? 'success' : 'error',
          bordered: false,
        },
        { default: () => (isActive ? 'Active' : 'Inactive') }
      );
    },
  },
  {
    title: 'Role',
    key: 'is_admin',
    sorter: true,
    render(row): VNode {
      return h(
        NTag,
        {
          type: row.permissions.is_platform_admin ? 'warning' : 'info',
          bordered: false,
        },
        { default: () => (row.permissions.is_platform_admin ? 'Admin' : 'User') }
      );
    },
  },
  {
    title: 'Created At',
    key: 'created_at',
    sorter: true,
    render(row): string {
      try {
        // Format the date
        const date = new Date(row.created_at);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
      } catch {
        return 'Invalid date';
      }
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    fixed: 'right',
    width: 100,
    render(row): VNode {
      return h(
        NSpace,
        {
          align: 'center',
          justify: 'center',
          size: [8, 0],
          inline: true,
          wrap: false,
        },
        {
          default: () => [
            h(
              NButton,
              {
                quaternary: true,
                size: 'small',
                onClick: () => {
                  openEditModal(row);
                },
              },
              { icon: () => h(NIcon, null, { default: () => h(IconEdit) }) }
            ),
            // Delete button - only shown for non-admin users
            !row.permissions.is_platform_admin &&
              h(
                NPopconfirm,
                {
                  onPositiveClick: () => deleteUser(row.uid),
                },
                {
                  default: () =>
                    'Are you sure you want to delete this user? They will be able to sign up again with the same email.',
                  trigger: () =>
                    h(
                      NButton,
                      {
                        quaternary: true,
                        size: 'small',
                        type: 'error',
                      },
                      { icon: () => h(NIcon, null, { default: () => h(IconDelete) }) }
                    ),
                }
              ),
          ],
        }
      );
    },
  },
];

// Add a computed property for default sorter
const defaultSorter = computed(() => {
  return {
    columnKey: sortKey.value,
    order: sortOrder.value === 'asc' ? 'ascend' : 'descend',
  };
});

// 6. Functions
function handlePageChange(page: number): void {
  currentPage.value = page;
  fetchUsers();
}

function handlePageSizeChange(size: number): void {
  pageSize.value = size;
  currentPage.value = 1; // Reset to first page when changing page size
  fetchUsers();
}

function handleSearch(): void {
  currentPage.value = 1; // Reset to first page when searching
  fetchUsers();
}

function handleSort(sorter: { columnKey: string; order: SortOrder }): void {
  // Set the sort key if provided
  if (sorter.columnKey) {
    sortKey.value = sorter.columnKey;
  }

  // Set the sort order based on order, defaulting to 'asc' if cleared
  if (sorter.order === false) {
    // When sorting is cleared, default to ascending
    sortOrder.value = 'asc';
  } else {
    sortOrder.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  }

  // Refresh the data
  fetchUsers();
}

function fetchUsers(): void {
  const query: SearchQuery = {
    text: searchQuery.value,
    limit: pageSize.value,
    offset: (currentPage.value - 1) * pageSize.value,
    sort_key: sortKey.value,
    sort_direction: sortOrder.value === 'asc' ? 'asc' : 'desc',
  };

  void store.fetchUsers(query);
}

// Delete user
async function deleteUser(uid: string): Promise<void> {
  try {
    const success = await store.deleteUser(uid);
    if (success) {
      message.success('User deleted successfully');
    }
  } catch (error) {
    message.error('Failed to delete user');
    logger.error('Error deleting user:', error);
  }
}

// Edit functions
function openEditModal(user: ExtendedUserInDb): void {
  currentEditId.value = user.uid;
  isEditingAdmin.value = user.permissions.is_platform_admin;

  // Populate form with current values
  currentEditEmail.value = user.email;
  editFormModel.value = {
    full_name: user.full_name,
    is_active: user.is_active,
  };

  showEditModal.value = true;
}

function cancelEditModal(): void {
  showEditModal.value = false;
  currentEditId.value = '';
}

function submitEditForm(): void {
  void editFormRef.value?.validate(errors => {
    void (async (): Promise<void> => {
      if (!errors) {
        const updateData: UserUpdateSuperuser = {
          full_name: editFormModel.value.full_name,
          is_active: editFormModel.value.is_active ?? true,
        };

        const result = await store.updateUser(currentEditId.value, updateData);

        if (result) {
          message.success('User updated successfully');
          showEditModal.value = false;
          currentEditId.value = '';
        }
      }
    })();
  });
}

// 7. Lifecycle hooks
onMounted((): void => {
  fetchUsers();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.adminArea }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.adminUsers }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18" :wrap="false">
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        Users Management
      </gaia-header>

      <!-- Search and Action Bar -->
      <NSpace justify="space-between" align="center" style="margin-bottom: 16px">
        <!-- Search Input -->
        <gaia-search-input
          v-model:value="searchQuery"
          placeholder="Search users"
          @search="handleSearch"
        />

        <!-- Action Buttons -->
        <NSpace>
          <NButton @click="fetchUsers">
            <template #icon>
              <NIcon>
                <IconRefresh />
              </NIcon>
            </template>
            Refresh
          </NButton>
        </NSpace>
      </NSpace>
    </n-flex>

    <NAlert v-if="store.error" type="error" :title="store.error" class="error-alert" />

    <div v-if="!store.loading && !store.hasUsers" class="empty-state">
      <NEmpty description="No users found" />
    </div>

    <gaia-table
      v-else
      :columns="columns"
      :data="store.users"
      :loading="store.loading"
      :pagination="false"
      striped
      :default-sort-order="defaultSorter"
      @update:sorter="handleSort"
    />

    <div v-if="store.hasUsers" class="pagination-container">
      <gaia-pagination
        :page="currentPage"
        :page-size="pageSize"
        :total="store.total"
        :page-sizes="[10, 20, 50]"
        @update:page="handlePageChange"
        @update:page-size="handlePageSizeChange"
      />
    </div>

    <!-- Edit User Modal -->
    <NModal
      v-model:show="showEditModal"
      preset="card"
      title="Edit User"
      style="width: 500px"
      :mask-closable="false"
    >
      <NForm
        ref="editFormRef"
        :model="editFormModel"
        :rules="formRules"
        label-placement="left"
        label-width="120"
        require-mark-placement="right-hanging"
      >
        <NFormItem label="Email">
          <gaia-input :value="currentEditEmail" disabled />
        </NFormItem>

        <NFormItem label="Full Name" path="full_name">
          <gaia-input v-model:value="editFormModel.full_name" placeholder="Enter full name" />
        </NFormItem>

        <NFormItem label="Status" path="is_active">
          <gaia-switch v-model:value="userIsActive">
            <template #checked>Active</template>
            <template #unchecked>Inactive</template>
          </gaia-switch>
          <div class="form-help-text">
            <div v-if="userIsActive">User can log in and access the system</div>
            <div v-else>User cannot log in and the email is blocked from signing up again</div>
          </div>
        </NFormItem>
      </NForm>

      <template #footer>
        <div class="modal-footer">
          <NButton @click="cancelEditModal">Cancel</NButton>
          <NButton type="primary" :loading="store.loading" @click="submitEditForm">
            Save Changes
          </NButton>
        </div>
      </template>
    </NModal>
  </Layout>
</template>

<style scoped>
.error-alert {
  margin-bottom: 16px;
}

.empty-state {
  padding: 30px 0;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

:deep(.n-input .n-input__input) {
  padding-right: 30px; /* Leave space for the clear button */
}

:deep(.n-data-table-th) {
  font-weight: 600;
}

:deep(.n-data-table-td) {
  padding: 12px 14px;
}

:deep(.n-button) {
  transition: all 0.2s ease;
}

.form-help-text {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 4px;
}

.form-help-warning {
  color: #ffa500;
}
</style>
